import { CurrencyPipe, DatePipe } from '@angular/common';
import { DEFAULT_CURRENCY_CODE, Inject, Injectable } from '@angular/core';
import { createSelector, select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { selectAppConfig } from 'src/app/core/application-config/application-config.selectors';
import { LocaleService } from 'src/app/core/locale/locale.service';
import {
	APPCONFIG_COMPULSORY_PAYMENT_DETAILS,
	APPCONFIG_CURRENCY_CODE,
} from 'src/app/shared/enums/application-config.enums';

@Injectable({
	providedIn: 'root',
})
export class DashboardLocalizationService {
	/**
	 * The currency code if overridden
	 */
	private _currencyCode?: string;
	/**
	 * The currency code if overridden
	 */
	private _enableCompulsoryPaymentDetails?: boolean;
	/**
	 * onDestroy, destroy it all
	 */
	private _destroy$ = new Subject<void>();
	/**
	 * The Dashboard Localization Service
	 * @param _defaultCurrencyCode the default currency code
	 * @param _store$ the store
	 * @param _locale the locale service
	 * @param _currency the currency pipe
	 * @param _datePipe	the date pipe
	 */
	constructor(
		@Inject(DEFAULT_CURRENCY_CODE) private _defaultCurrencyCode: string,
		private _store$: Store,
		private _locale: LocaleService,
		private _currency: CurrencyPipe,
		private _datePipe: DatePipe
	) {
		this._store$
			.pipe(
				select(
					createSelector(
						selectAppConfig([APPCONFIG_CURRENCY_CODE, APPCONFIG_COMPULSORY_PAYMENT_DETAILS]),
						(config) => ({
							config,
						})
					)
				),
				takeUntil(this._destroy$)
			)
			.subscribe(({ config }) => {
				this._currencyCode = config.currency_code || this._defaultCurrencyCode;
				this._enableCompulsoryPaymentDetails = config?.enable_compulsory_payment_details;
			});
	}
	/**
	 * Returns the localized string for the upcoming payment
	 */
	public generateLocalizedUpcomingPaymentString(
		paymentAmount: string,
		cardType: string,
		ccLastFour: string,
		paymentDate: string,
		remainingCompulsoryPayments?: string
	): string {
		const localizedPaymentAmount: string = this._currency.transform(+paymentAmount, this._currencyCode);
		const localizedPaymentDate: string = this._datePipe.transform(paymentDate, 'shortDate');
		if (this._enableCompulsoryPaymentDetails && remainingCompulsoryPayments) {
			const localizedRemainingAmount: string = this._currency.transform(
				+remainingCompulsoryPayments,
				this._currencyCode
			);

			return (
				this._locale
					.get('dashboard.upcoming_payment')
					.replace('@@paymentAmount', localizedPaymentAmount)
					.replace('@@cardType', cardType.toLowerCase())
					.replace('@@ccLastFour', ccLastFour)
					.replace('@@paymentDate', localizedPaymentDate) +
				' ' +
				this._locale
					.get('dashboard.remaining_compulsory_payments')
					.replace('@@paymentAmount', localizedRemainingAmount)
			);
		} else {
			return this._locale
				.get('dashboard.upcoming_payment')
				.replace('@@paymentAmount', localizedPaymentAmount)
				.replace('@@cardType', cardType.toLowerCase())
				.replace('@@ccLastFour', ccLastFour)
				.replace('@@paymentDate', localizedPaymentDate);
		}
	}
}
